"use client";
import { useState, useRef, useEffect, Suspense } from "react";
import { GiCalculator } from "react-icons/gi";
import { FaDatabase } from "react-icons/fa";
import PopupModal from "../../component/PopupModal";
import RateCalculatorForm from "../../component/RateCalculatorForm";
import RateResultsGrid from "../../component/RateResultsGrid";
import BoxInputs from "../../component/BoxInputs";
import SummaryHeader from "../../component/SummaryHeader";
import InternationalRateCalculatorForm from "../../component/InternationalRateCalculatorForm";
import { useRouter, useSearchParams } from "next/navigation";
import API_URLS from "@/component/apiUrls";

function RateCalculatorContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [insuranceOption, setInsuranceOption] = useState("ownerRisk");
  const [selfDropOption, setSelfDropOption] = useState("Pickup");
  const [invoiceValue, setInvoiceValue] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [boxes, setBoxes] = useState([
    {
      noOfBoxes: "",
      Weightperbox: "",
      length: "",
      height: "",
      width: "",
    },
  ]);
  const [showPopup, setShowPopup] = useState(false);
  const [expandedCards, setExpandedCards] = useState({}); // key: index, value: true/false
  const [paymentMode, setPaymentMode] = useState("Prepaid");
  const [packageType, setPackageType] = useState("B2B");
  const [loading, setLoading] = useState(false);
  const [pickupPincode, setPickupPincode] = useState("");
  const [deliveryPincode, setDeliveryPincode] = useState("");
  const [codAmount, setCodAmount] = useState("");
  const [rates, setRates] = useState([]);
  const [networkError, setNetworkError] = useState("");
  const [calculationDone, setCalculationDone] = useState(false);
  const number = "+91 9971471214";
  const [activeTab, setActiveTab] = useState("Domestic");

  // Set tab from URL on mount
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && (tabParam === "Domestic" || tabParam === "International")) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Tab change handler that updates URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCalculationDone(false); // Reset calculation state on tab switch
    setRates([]); // Clear previous rates on tab switch
    // Update URL query param
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    params.set("tab", tab);
    router.replace(`?${params.toString()}`);
  };

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  // Unified error state
  const [errors, setErrors] = useState({
    pickupPincode: "",
    deliveryPincode: "",
    codAmount: "",
    invoiceValue: "",
    boxes: [],
  });

  useEffect(() => {
    if (showAdvanced) {
      // Switching to Advanced - keep existing boxes
      if (boxes.length === 0) {
        setBoxes([
          {
            noOfBoxes: "",
            Weightperbox: "",
            length: "",
            height: "",
            width: "",
          },
        ]);
      }
    } else {
      // Switching to Basic - ensure we have one box
      setBoxes([
        {
          noOfBoxes: "",
          Weightperbox: "",
          length: "",
          height: "",
          width: "",
        },
      ]);
    }
  }, [showAdvanced]);

  const lspArray = [
    {
      lspId: "delhivery",
      lspImagePath: "/image/photoses.jpg",
      alt: "Delhivery",
      companyName: "Delhivery",
    },
    {
      lspId: "Amazon",
      lspImagePath: "/image/amazon logo (1).png",
      alt: "Amazon",
      companyName: "Amazon",
    },
    {
      lspId: "BlueDart",
      lspImagePath: "/image/bluedarte.png",
      alt: "bluedart",
      companyName: "Blue Dart",
    },
    {
      lspId: "DTDC",
      lspImagePath: "/image/dtdtc.png",
      alt: "dtdc",
      companyName: "DTDC",
    },
    {
      lspId: "Ekart",
      lspImagePath: "/image/ekartts.png",
      alt: "ekart",
      companyName: "Ekart",
    },
    {
      lspId: "Indiapost",
      lspImagePath: "/image/indianpost (1).png",
      alt: "Indianpost",
      companyName: "India Post",
    },
    {
      lspId: "IndiaPost Business Parcel",
      lspImagePath: "/image/indianpost (1).png",
      alt: "Indianpost",
      companyName: "India Post",
    },
    {
      lspId: "IndiaPost SpeedPost",
      lspImagePath: "/image/indianpost (1).png",
      alt: "Indianpost",
      companyName: "India Post",
    },

    {
      lspId: "Movin",
      lspImagePath: "/image/movin2.png",
      alt: "movin",
      companyName: "Movin",
    },
    {
      lspId: "XpressBees",
      lspImagePath: "/image/expressone.png",
      alt: "movin",
      companyName: "Movin",
    },
    {
      lspId: "Shadowfax",
      lspImagePath: "/image/shadowfx.png",
      alt: "Shadowfax",
      companyName: "Shadowfax",
    },
    {
      lspId: "criticalog",
      lspImagePath: "/image/criticalog-logo.png",
      alt: "criticalog",
      companyName: "criticalog",
    },
  ];

  const handleBoxChange = (index, field, value) => {
    const newBoxes = [...boxes];
    newBoxes[index][field] = value;
    setBoxes(newBoxes);

    // Clear error for this specific field
    const newErrors = { ...errors };
    if (newErrors.boxes[index]) {
      newErrors.boxes[index] = { ...newErrors.boxes[index], [field]: "" };
      setErrors(newErrors);
    }
  };

  const addBox = () => {
    setBoxes([
      ...boxes,
      {
        noOfBoxes: "",
        Weightperbox: "",
        length: "",
        height: "",
        width: "",
      },
    ]);

    // Add error object for new box
    const newErrors = { ...errors };
    newErrors.boxes.push({
      noOfBoxes: "",
      Weightperbox: "",
      length: "",
      height: "",
      width: "",
    });
    setErrors(newErrors);
  };

  const deleteBox = (index) => {
    const newBoxes = boxes.filter((_, i) => i !== index);
    setBoxes(newBoxes);

    // Remove error object for deleted box
    const newErrors = { ...errors };
    newErrors.boxes.splice(index, 1);
    setErrors(newErrors);
  };

  const handleToggleExpand = (index) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const isValidPincode = (pin) => pin.length === 6 && /^\d{6}$/.test(pin);

  // Function to check if all required fields are filled
  const areAllFieldsFilled = () => {
    // Check pincodes
    if (
      !pickupPincode ||
      !deliveryPincode ||
      !isValidPincode(pickupPincode) ||
      !isValidPincode(deliveryPincode)
    ) {
      return false;
    }

    // Check COD amount if COD is selected
    if (paymentMode === "COD" && (!codAmount || Number(codAmount) < 1)) {
      return false;
    }

    // Check the  invoice value if carrr risk is selected
    if (
      insuranceOption === "carrierRisk" &&
      (!invoiceValue || Number(invoiceValue) <= 0)
    ) {
      return false;
    }

    // Check boxes
    for (let box of boxes) {
      // Check weight
      if (!box.Weightperbox || Number(box.Weightperbox) <= 0) {
        return false;
      }

      // Check dimensions
      if (!box.length || Number(box.length) <= 0) {
        return false;
      }
      if (!box.width || Number(box.width) <= 0) {
        return false;
      }
      if (!box.height || Number(box.height) <= 0) {
        return false;
      }

      // Check quantity for advanced mode
      if (showAdvanced && (!box.noOfBoxes || Number(box.noOfBoxes) <= 0)) {
        return false;
      }
    }

    return true;
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {
      pickupPincode: "",
      deliveryPincode: "",
      codAmount: "",
      invoiceValue: "",
      boxes: [],
    };

    // Validate pincodes
    if (!pickupPincode) {
      newErrors.pickupPincode = "Pickup pincode is required";
    } else if (!isValidPincode(pickupPincode)) {
      newErrors.pickupPincode = "Please enter a valid 6-digit pickup pincode";
    }

    if (!deliveryPincode) {
      newErrors.deliveryPincode = "Delivery pincode is required";
    } else if (!isValidPincode(deliveryPincode)) {
      newErrors.deliveryPincode =
        "Please enter a valid 6-digit delivery pincode";
    }

    // Validate COD amount
    if (paymentMode === "COD") {
      if (!codAmount) {
        newErrors.codAmount = "COD amount is required";
      } else if (Number(codAmount) < 1) {
        newErrors.codAmount = "COD amount must be at least ₹1";
      }
    }

    // Validate invoice value for carrier risk
    if (insuranceOption === "carrierRisk") {
      if (!invoiceValue) {
        newErrors.invoiceValue = "Invoice value is required";
      } else if (Number(invoiceValue) <= 0) {
        newErrors.invoiceValue = "Invoice value must be greater than 0";
      }
    }

    // Validate boxes
    newErrors.boxes = boxes.map((box, index) => {
      const boxErrors = {
        noOfBoxes: "",
        Weightperbox: "",
        length: "",
        height: "",
        width: "",
      };

      // Validate quantity for advanced mode
      if (showAdvanced) {
        if (!box.noOfBoxes) {
          boxErrors.noOfBoxes = "Quantity is required";
        } else if (Number(box.noOfBoxes) <= 0) {
          boxErrors.noOfBoxes = "Quantity must be greater than 0";
        }
      }

      // Validate weight
      if (!box.Weightperbox) {
        boxErrors.Weightperbox = "Weight is required";
      } else if (Number(box.Weightperbox) <= 0) {
        boxErrors.Weightperbox = "Weight must be greater than 0";
      }

      // Validate dimensions (required fields)
      if (!box.length) {
        boxErrors.length = "Length is required";
      } else if (Number(box.length) <= 0) {
        boxErrors.length = "Length must be greater than 0";
      }
      if (!box.width) {
        boxErrors.width = "Width is required";
      } else if (Number(box.width) <= 0) {
        boxErrors.width = "Width must be greater than 0";
      }
      if (!box.height) {
        boxErrors.height = "Height is required";
      } else if (Number(box.height) <= 0) {
        boxErrors.height = "Height must be greater than 0";
      }

      return boxErrors;
    });

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) =>
      typeof error === "string"
        ? error
        : error.some((boxError) =>
          Object.values(boxError).some((fieldError) => fieldError)
        )
    );
  };

  const fetchRates = async () => {
    setNetworkError("");
    if (!validateForm()) {
      return;
    }

    // Create the paramsData object for URL parameters
    const paramsData = {
      originPincode: pickupPincode,
      destinationPincode: deliveryPincode,
      serviceCategory: packageType.toLowerCase(),
      riskType: insuranceOption,
      isGSTinclusiv: false,
      isCOD: paymentMode === "COD",
      selfDrop: selfDropOption === "Self-drop",
      codAmount: paymentMode === "COD" ? Number(codAmount) : 0,
      weightDetailsArray: boxes.map((box) => {
        return {
          weightKg: box.Weightperbox,
          lengthCm: box.length,
          breadthCm: box.width,
          heightCm: box.height,
          quantity: showAdvanced ? box.noOfBoxes : undefined,
        };
      }),
      ...(insuranceOption === "carrierRisk" && invoiceValue
        ? { invoiceValue: Number(invoiceValue) }
        : {}),
    };

    // Create the URL without paramsData
    const url = `http://localhost:4114/orders/add/`;

    // Log the URL for debugging
    console.log("Generated URL:", url);

    const requestBody = {
      originPincode: pickupPincode,
      destinationPincode: deliveryPincode,
      serviceCategory: packageType.toLowerCase(),
      riskType: insuranceOption,
      isGSTinclusiv: false,
      isCOD: paymentMode === "COD",
      selfDrop: selfDropOption === "Self-drop",
      codAmount: paymentMode === "COD" ? Number(codAmount) : 0,
      ...(insuranceOption === "riskType" && {
        invoiceValue: Number(invoiceValue) || 0,
      }),
      weightDetailsArray: boxes.map((box) => {
        return {
          weightKg: box.Weightperbox,
          lengthCm: box.length,
          breadthCm: box.width,
          heightCm: box.height,
          quantity: showAdvanced ? box.noOfBoxes : undefined,
        };
      }),
      ...(insuranceOption === "carrierRisk" && invoiceValue
        ? { invoiceValue: Number(invoiceValue) }
        : {}),
    };

    setLoading(true);
    try {
      const res = await fetch(API_URLS.rateOptions, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          JSON.stringify(data.message, null, 2) || `Server Error: ${res.status}`
        );
      }

      setRates(data?.rateOptions || []);
      setCalculationDone(true); // Set calculation done on success
    } catch (err) {
      console.error(err);
      setNetworkError("Something went wrong: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pickupPincode && isValidPincode(pickupPincode)) {
      setErrors((prev) => ({ ...prev, pickupPincode: "" }));
    }
  }, [pickupPincode]);

  useEffect(() => {
    if (deliveryPincode && isValidPincode(deliveryPincode)) {
      setErrors((prev) => ({ ...prev, deliveryPincode: "" }));
    }
  }, [deliveryPincode]);

  useEffect(() => {
    if (codAmount && Number(codAmount) >= 1) {
      setErrors((prev) => ({ ...prev, codAmount: "" }));
    }
  }, [codAmount]);

  useEffect(() => {
    if (invoiceValue && Number(invoiceValue) > 0) {
      setErrors((prev) => ({ ...prev, invoiceValue: "" }));
    }
  }, [invoiceValue]);

  useEffect(() => {
    const newErrors = { ...errors };
    let hasChanges = false;

    boxes.forEach((box, index) => {
      if (newErrors.boxes[index]) {
        // Clear length error
        if (
          box.length &&
          Number(box.length) > 0 &&
          newErrors.boxes[index].length
        ) {
          newErrors.boxes[index] = { ...newErrors.boxes[index], length: "" };
          hasChanges = true;
        }
        // Clear width error
        if (
          box.width &&
          Number(box.width) > 0 &&
          newErrors.boxes[index].width
        ) {
          newErrors.boxes[index] = { ...newErrors.boxes[index], width: "" };
          hasChanges = true;
        }
        // Clear height error
        if (
          box.height &&
          Number(box.height) > 0 &&
          newErrors.boxes[index].height
        ) {
          newErrors.boxes[index] = { ...newErrors.boxes[index], height: "" };
          hasChanges = true;
        }
        // Clear weight error
        if (
          box.Weightperbox &&
          Number(box.Weightperbox) > 0 &&
          newErrors.boxes[index].Weightperbox
        ) {
          newErrors.boxes[index] = {
            ...newErrors.boxes[index],
            Weightperbox: "",
          };
          hasChanges = true;
        }
        // Clear quantity error
        if (
          box.noOfBoxes &&
          Number(box.noOfBoxes) > 0 &&
          newErrors.boxes[index].noOfBoxes
        ) {
          newErrors.boxes[index] = { ...newErrors.boxes[index], noOfBoxes: "" };
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setErrors(newErrors);
    }
  }, [boxes]);

  // Helper to clear rates on any field change
  const handleFieldChange = (setter) => (value) => {
    setter(value);
    setRates([]);
    setCalculationDone(false); // Reset calculation state on any change
  };

  // Helper to reset everything
  const handleReset = () => {
    setCalculationDone(false);
    setRates([]);
  };

  return (
    <>
      <div className="min-h-96 bg-white p-4 md:p-8 font-sans">
        {/* <div className="w-full pt-10  ">
          {calculationDone && (
            <SummaryHeader
              pickupPincode={pickupPincode}
              deliveryPincode={deliveryPincode}
              packageType={packageType}
              boxes={boxes}
              paymentMode={paymentMode}
              codAmount={codAmount}
              insuranceOption={insuranceOption}
              invoiceValue={invoiceValue}
              handleReset={handleReset}
            />
          )}
        </div> */}
        <div className="max-w-7xl mx-auto pt-20">
          <h1 className="text-4xl md:text-4xl font-extrabold text-blue-900 flex items-center gap-4 mb-8 select-none drop-shadow-lg">
            <GiCalculator className="text-4xl text-blue-900 animate-pulse" />
            Rate Calculator
          </h1>

          {/* Tabs and Popup */}
          <div className="relative">
            <div className="flex flex-wrap gap-5  justify-center md:justify-start ">
              {["Domestic", "International"].map((tab) => (
                <button
                  key={tab}
                  className={`px-6 py-2 rounded-md font-semibold cursor-pointer transition-all duration-300 shadow-md border-2  ${activeTab === tab
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                    }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
            {/* <PopupModal show={showPopup} onClose={() => setShowPopup(false)} /> */}
          </div>

          <div className="w-full pt-10  ">
            {calculationDone && (
              <SummaryHeader
                pickupPincode={pickupPincode}
                deliveryPincode={deliveryPincode}
                packageType={packageType}
                boxes={boxes}
                paymentMode={paymentMode}
                codAmount={codAmount}
                insuranceOption={insuranceOption}
                invoiceValue={invoiceValue}
                handleReset={handleReset}
              />
            )}
          </div>

          {/* Conditionally render form or nothing (header is now above) */}
          {!calculationDone && activeTab === "Domestic" && (
            <div className="bg-white rounded-2xl p-6 md:p-10  border border-indigo-200  duration-500 min-h-[500px] mb-10 w-full">
              <RateCalculatorForm
                pickupPincode={pickupPincode}
                setPickupPincode={handleFieldChange(setPickupPincode)}
                deliveryPincode={deliveryPincode}
                setDeliveryPincode={handleFieldChange(setDeliveryPincode)}
                packageType={packageType}
                setPackageType={handleFieldChange(setPackageType)}
                showAdvanced={showAdvanced}
                setShowAdvanced={handleFieldChange(setShowAdvanced)}
                boxes={boxes}
                errors={errors}
                paymentMode={paymentMode}
                setPaymentMode={handleFieldChange(setPaymentMode)}
                codAmount={codAmount}
                setCodAmount={handleFieldChange(setCodAmount)}
                insuranceOption={insuranceOption}
                setInsuranceOption={handleFieldChange(setInsuranceOption)}
                invoiceValue={invoiceValue}
                setInvoiceValue={handleFieldChange(setInvoiceValue)}
                selfDropOption={selfDropOption}
                setSelfDropOption={handleFieldChange(setSelfDropOption)}
                handleBoxChange={(index, field, value) => {
                  handleBoxChange(index, field, value);
                  setRates([]);
                  setCalculationDone(false);
                }}
                addBox={() => {
                  addBox();
                  setRates([]);
                  setCalculationDone(false);
                }}
                deleteBox={(index) => {
                  deleteBox(index);
                  setRates([]);
                  setCalculationDone(false);
                }}
                onCalculate={fetchRates}
              />
            </div>
          )}
          {/* International Form */}
          {!calculationDone && activeTab === "International" && (
            <div className="">
              <InternationalRateCalculatorForm />
            </div>
          )}

          {/* Results below, grid layout */}
          <div className="w-full">
            {networkError && (
              <div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 w-full max-w-full overflow-x-auto"
                role="alert"
              >
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline break-words">
                  {networkError}
                </span>
              </div>
            )}

            {rates.length > 0 ? (
              <div className="h-auto my-2 text-gray-800 mb-2 flex flex-col justify-center items-start lg:flex-row lg:items-center lg:justify-start gap-1">
                <strong>For Signing Contract Please Contact Us: </strong>
                <span>

                  <a className="text-blue-600 underline">
                    sales@sureship.in
                  </a>
                </span>
              </div>
            ) : null}

            {!areAllFieldsFilled() ? null : loading ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm animate-fade-in">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 border-[5px] border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-700 font-semibold text-lg animate-pulse">
                    Loading...
                  </span>
                </div>
              </div>
            ) : (
              <RateResultsGrid
                rates={rates}
                lspArray={lspArray}
                packageType={packageType}
                pickupPincode={pickupPincode}
                deliveryPincode={deliveryPincode}
                boxes={boxes}
                expandedCards={expandedCards}
                onToggleExpand={handleToggleExpand}
                insuranceOption={insuranceOption}
                paymentMode={paymentMode}
                codAmount={codAmount}
                invoiceValue={invoiceValue}
                selfDropOption={selfDropOption}
                showAdvanced={showAdvanced}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Loading component for Suspense fallback
function RateCalculatorLoading() {
  return (
    <div className="min-h-96 bg-white p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto pt-20">
        <h1 className="text-4xl md:text-4xl font-extrabold text-blue-900 flex items-center gap-4 mb-8 select-none drop-shadow-lg">
          <GiCalculator className="text-4xl text-blue-900 animate-pulse" />
          Rate Calculator
        </h1>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 border-[5px] border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-700 font-semibold text-lg animate-pulse">
              Loading...
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={<RateCalculatorLoading />}>
      <RateCalculatorContent />
    </Suspense>
  );
}
